import type { DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/react";
import { useI18nContext } from "@procore/core-react";
import { useToastAlertContext } from "@procore/toast-alert";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ActiveRow, CategoryNodeData, ProjectCategory, ProjectCategoryNode } from "./types";
import {
   CATEGORIES_TRANSLATIONS,
   detectDropZone,
   DropZoneIntent,
   getCategoryMoveUpdate,
   isCategoryInDraggedTree,
   MAX_DEPTH,
   resolveAfterDropTarget,
   type CategoryDropData,
   type CategoryMoveUpdate,
} from "./utils";

const { BEFORE, AFTER } = DropZoneIntent;

type UseCategoryDragOptions = {
   categories: ProjectCategory[];
   flatRows: ProjectCategoryNode[];
   activeRow: ActiveRow | null;
   isMoving: boolean;
   isSearching: boolean;
   onMove: (update: CategoryMoveUpdate) => void;
};

export const useCategoryDrag = ({
   categories,
   flatRows,
   activeRow,
   isMoving,
   isSearching,
   onMove,
}: UseCategoryDragOptions) => {
   const I18n = useI18nContext();
   const { showToast } = useToastAlertContext();
   const [dragSource, setDragSource] = useState<CategoryNodeData | null>(null);
   const [dropTarget, setDropTarget] = useState<CategoryDropData | null>(null);

   // Refs mirror state/props so drag handlers always read the latest values,
   // avoiding stale closures when DragDropProvider captures an old callback.
   // NOTE: These are a workaround.
   const dragSourceRef = useRef(dragSource);
   dragSourceRef.current = dragSource;
   const dropTargetRef = useRef(dropTarget);
   dropTargetRef.current = dropTarget;
   const categoriesRef = useRef(categories);
   categoriesRef.current = categories;
   const flatRowsRef = useRef(flatRows);
   flatRowsRef.current = flatRows;

   const dragEnabled = !isSearching && activeRow === null && !isMoving && categories.length > 0;

   const handleDragStart = useCallback((...[event]: Parameters<DragStartEvent>) => {
      setDragSource((event.operation.source?.data as CategoryNodeData) ?? null);
      setDropTarget(null);
   }, []);

   const handleDragMove = useCallback((...[event]: Parameters<DragMoveEvent>): void => {
      const { source, target } = event.operation;

      if (!source || !target?.element) {
         setDropTarget(null);
         return;
      }

      const dragId = String(source.id);
      const targetId = String(target.id);

      if (isCategoryInDraggedTree(categoriesRef.current, dragId, targetId)) {
         setDropTarget(null);
         return;
      }

      const rect = target.element.getBoundingClientRect();
      const pointerY = lastPointerY.current;
      const data = target.data as CategoryNodeData;

      if (pointerY == null) {
         setDropTarget({ ...data, intent: BEFORE });
         return;
      }

      const intent = detectDropZone(pointerY, rect, data.depth !== MAX_DEPTH);

      if (intent === AFTER) {
         const resolved = resolveAfterDropTarget(
            data,
            flatRowsRef.current,
            lastPointerX.current,
            rect,
         );
         if (resolved) {
            setDropTarget(resolved);
            return;
         }
      }

      setDropTarget({ ...data, intent });
   }, []);

   const handleDragEnd = useCallback(
      (...[event]: Parameters<DragEndEvent>) => {
         const source = dragSourceRef.current;
         const target = dropTargetRef.current;

         setDragSource(null);
         setDropTarget(null);

         if (event.canceled || !source || !target) return;

         try {
            const update = getCategoryMoveUpdate(categoriesRef.current, source, target);
            onMove(update);
         } catch (e) {
            if (e instanceof Error && e.message === "move_max_depth") {
               showToast.error(
                  I18n.t(`${CATEGORIES_TRANSLATIONS}.${e.message}`, {
                     count: MAX_DEPTH,
                     max_depth: MAX_DEPTH,
                  }),
               );
            }
         }
      },
      [onMove, showToast, I18n],
   );

   // Track pointer position globally so we can read it in drag handlers.
   // dnd-kit tells us *which* element we're over but not *where* on it.
   const lastPointerX = useRef<number | null>(null);
   const lastPointerY = useRef<number | null>(null);
   useEffect(() => {
      if (!dragEnabled) return;

      const handler = (e: PointerEvent) => {
         lastPointerX.current = e.clientX;
         lastPointerY.current = e.clientY;
      };
      document.addEventListener("pointermove", handler, { passive: true });
      return () => {
         document.removeEventListener("pointermove", handler);
         lastPointerX.current = null;
         lastPointerY.current = null;
      };
   }, [dragEnabled]);

   const afterIndicatorPosition: { index: number; depth: number } | null = (() => {
      if (!dragEnabled || !dropTarget || dropTarget.intent !== AFTER) return null;

      const targetIndex = flatRows.findIndex((r) => r.id === dropTarget.id);
      if (targetIndex === -1) return null;

      const firstNonDescendantIndex = flatRows.findIndex(
         (r, i) => i > targetIndex && r.depth <= dropTarget.depth,
      );
      const effectiveIndex =
         firstNonDescendantIndex === -1 ? flatRows.length - 1 : firstNonDescendantIndex - 1;
      return { index: effectiveIndex, depth: dropTarget.depth };
   })();

   return {
      dragEnabled,
      dragSource,
      dropTarget,
      afterIndicatorPosition,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
   };
};
